import React from 'react';

import { Chip, Typography } from '@mui/material';
import { AlineTooltip } from 'src/components';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(theme => ({
  badge: {
    backgroundColor: theme.appColors.background,
    color: theme.palette.text.secondary,
    flexShrink: 0,
    fontSize: 12,
  },
  // Matches the bar the chat shows over a deleted AI draft.
  notice: {
    backgroundColor: theme.palette.warning.main,
    color: theme.palette.warning.contrastText,
    display: 'block',
    padding: theme.spacing(0.5, 2),
  },
}));

/** A skill missing its name or description: it can't be tested or published yet. */
export const SkillDraftBadge = React.memo(function SkillDraftBadge({
  description,
  name,
}: {
  description: string;
  name: string;
}) {
  const { classes } = useStyles();
  return (
    <AlineTooltip arrow placement="top" title={getDraftHint({ description, name })}>
      <Chip className={classes.badge} label="Incomplete" size="small" />
    </AlineTooltip>
  );
});

/** The bar under the editor's file header while the author has something left to publish. */
export const SkillPublishNotice = React.memo(function SkillPublishNotice({
  hasUnpublishedChanges,
  isPublished,
}: {
  hasUnpublishedChanges: boolean;
  isPublished: boolean;
}) {
  const { classes } = useStyles();
  if (isPublished && !hasUnpublishedChanges) return null;

  return (
    <Typography align="center" className={classes.notice} role="status" variant="caption">
      {isPublished
        ? 'You have unpublished changes. Others use the published version until you publish them.'
        : 'This skill is not published. Only you can see it until you publish it.'}
    </Typography>
  );
});

/** Shown to the author: a skill never published, or one with changes not published yet. */
export const SkillPublishBadge = React.memo(function SkillPublishBadge({
  hasUnpublishedChanges,
  isPublished,
}: {
  hasUnpublishedChanges: boolean;
  isPublished: boolean;
}) {
  const { classes } = useStyles();
  if (isPublished && !hasUnpublishedChanges) return null;
  const label = isPublished ? 'Unpublished changes' : 'Unpublished';
  const hint = isPublished
    ? 'Others use the published version until you publish your changes.'
    : 'Only you can see this skill until you publish it.';
  return (
    <AlineTooltip arrow placement="top" title={hint}>
      <Chip className={classes.badge} label={label} size="small" />
    </AlineTooltip>
  );
});

function getDraftHint({ description, name }: { description: string; name: string }): string {
  const hasName = Boolean(name.trim());
  const hasDescription = Boolean(description.trim());
  if (!hasName && !hasDescription) return 'Add a name and description to run this skill.';
  if (!hasName) return 'Add a name to run this skill.';
  if (!hasDescription) return 'Add a description to run this skill.';
  return 'Review this skill before running it.';
}
